/**
 * Zoe conversations — list, resume and delete the requesting user's own
 * conversations. Every route is scoped to the signed-in user server-side;
 * another user's conversation is simply "not found".
 */
import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from '../utils/apiClient';
import type {
  ConversationMessagesPage,
  ConversationSummary,
} from '../types/zoeConversations';

const OPS_AGENT = `${API_ENDPOINTS.base || ''}/api/operations-agent`;

async function readError(res: Response, fallback: string): Promise<Error> {
  const body = (await res.json().catch(() => null)) as { error?: string } | null;
  return new Error(body?.error || `${fallback} (${res.status})`);
}

/** The user's recent conversations, most recently active first. */
export async function listConversations(limit = 20): Promise<ConversationSummary[]> {
  const res = await authenticatedFetch(`${OPS_AGENT}/conversations?limit=${limit}`);
  if (!res.ok) throw await readError(res, 'Could not load conversations');
  const json = (await res.json()) as { conversations: ConversationSummary[] };
  return json.conversations;
}

/**
 * One page of a conversation — the latest by default. Pass `before` (a
 * `turn_id`) to page back through older messages.
 */
export async function fetchConversationMessages(
  conversationId: string,
  options: { before?: string; limit?: number } = {},
): Promise<ConversationMessagesPage> {
  const qs = new URLSearchParams();
  if (options.limit) qs.set('limit', String(options.limit));
  if (options.before) qs.set('before', options.before);
  const query = qs.toString();
  const res = await authenticatedFetch(
    `${OPS_AGENT}/conversations/${encodeURIComponent(conversationId)}/messages${query ? `?${query}` : ''}`,
  );
  if (res.status === 404) throw new ConversationNotFoundError(conversationId);
  if (!res.ok) throw await readError(res, 'Could not load the conversation');
  return (await res.json()) as ConversationMessagesPage;
}

/** Permanently deletes a conversation and everything recorded from it. */
export async function deleteConversation(conversationId: string): Promise<void> {
  const res = await authenticatedFetch(`${OPS_AGENT}/conversations/${encodeURIComponent(conversationId)}`, {
    method: 'DELETE',
  });
  // Already gone is the outcome the caller wanted.
  if (res.status === 404) return;
  if (!res.ok) throw await readError(res, 'Could not delete the conversation');
}

/**
 * The conversation no longer exists for this user — deleted, or from another
 * session. Distinct from a failure so a caller can quietly start fresh
 * instead of showing an error.
 */
export class ConversationNotFoundError extends Error {
  constructor(public readonly conversationId: string) {
    super('Conversation not found');
    this.name = 'ConversationNotFoundError';
  }
}
